import jsPDF from 'jspdf';
import { MutableRefObject } from 'react';
import i18n from 'i18next';
import { IProjectsOverview } from 'client/common/interfaces/project.ts';
import { round, roundToFixed } from '@oliasoft-open-source/units';
import translations from 'src/internationalisation/translation-map.json';
import { formatToMillions } from 'client/common/format-data/format-to-millions.ts';
import { getMean } from 'views/simulations/result-overview/result-utils';
import '../fonts/Roobert-Regular-normal';
import { addChart } from 'views/report/utils/add-chart/add-chart.ts';
import { addTable } from 'views/report/utils/add-table/add-table.ts';
import { Operation } from 'client/common/interfaces/activity-model.ts';
import { TRiskFilter } from 'views/report/report.tsx';
import { addTitle } from 'views/report/utils/add-title/add-title.ts';
import { fontNormal, fontSecondarySize, fontSmallSize, lineInterval } from 'views/report/utils/style-consts.ts';
import { getCenterXPosition } from 'views/report/utils/utils.ts';

const getRisksTableData = (operations: Operation[], riskFilter: TRiskFilter) => {
  const filterMap = riskFilter.reduce((acc, curr) => {
    return { ...acc, [curr.value || '']: curr.selected };
  }, {});
  const simulationIds = Object.keys(filterMap);

  const headerRow = [
    'Risk',
    `${i18n.t(translations.probability)} (%)`,
    `${i18n.t(translations.simulations_meanTime)} (hrs)`,
  ];

  const rows = operations
    .filter(
      (operation) =>
        simulationIds.includes(operation.operationId) && simulationIds.includes(operation.projectSectionId),
    )
    .reduce((riskData: any, operation) => {
      const operationTaskData = operation.tasks
        .filter((task) => !task.isBranch && task.parentId !== null)
        .map((task) => ({
          name: task.name,
          certainty: task.certainty,
          mean: getMean(task, operation.tasks),
        }));
      return [...riskData, ...operationTaskData];
    }, [])
    .map((item: any) => {
      const { name, certainty, mean } = item;
      return [{ content: name, styles: { fontStyle: 'bold' } }, certainty, roundToFixed(mean, 2)];
    });

  return [headerRow, ...rows];
};

type TData = {
  doc: jsPDF;
  padding: number;
  forecastLogoHeight: number;
  timeVsDepthChartRef: MutableRefObject<null>;
  costVsDepthChartRef: MutableRefObject<null>;
  simulationsResult: any;
  projectOverview: IProjectsOverview;
  operations: Operation[];
  riskFilter: TRiskFilter;
};

export const addTimeAndCostVsDepth = async ({
  doc,
  padding,
  forecastLogoHeight,
  timeVsDepthChartRef,
  costVsDepthChartRef,
  simulationsResult,
  operations,
  riskFilter,
}: TData) => {
  const totalTimeMain = `${round(simulationsResult?.average / 24, 1)} ${i18n.t(translations.days).toLocaleLowerCase()}`;
  const totalCostMain = `${formatToMillions(simulationsResult?.average_cost)} m`;

  doc.addPage();

  const { y } = addTitle(doc, i18n.t(translations.timeTracker_totalAFE), {
    x: padding,
    y: forecastLogoHeight + padding + lineInterval,
  });

  const timeTitle = addTitle(doc, i18n.t(translations.time), {
    font: fontNormal,
    fontSize: fontSmallSize,
    x: padding,
    y: y + padding,
  });
  const totalTimeTitle = addTitle(doc, totalTimeMain, {
    font: fontNormal,
    fontSize: fontSecondarySize,
    x: padding,
    y: timeTitle.y + 7,
  });
  await addChart(doc, timeVsDepthChartRef, {
    useCORS: true,
    height: 90,
    width: 95,
    positionX: padding,
    positionY: totalTimeTitle.y + 5,
  });

  addTitle(doc, i18n.t(translations.cost), {
    font: fontNormal,
    fontSize: fontSmallSize,
    x: getCenterXPosition(doc),
    y: y + padding,
  });
  addTitle(doc, totalCostMain, {
    font: fontNormal,
    fontSize: fontSecondarySize,
    x: getCenterXPosition(doc),
    y: timeTitle.y + 7,
  });
  const costVsDepthChart = await addChart(doc, costVsDepthChartRef, {
    useCORS: true,
    height: 90,
    width: 95,
    positionX: getCenterXPosition(doc),
    positionY: totalTimeTitle.y + 5,
  });

  addTable(doc, getRisksTableData(operations, riskFilter), {
    title: i18n.t(translations.risks_risks),
    y: costVsDepthChart.y + costVsDepthChart.chartHeight + padding,
    headersCount: 1,
    aligns: ['left', 'right', 'right'],
    hasUnitsRow: false,
  });
};
